import React from 'react';

// THE CIVICLENS BRAND (Cameron, 2026-09-19), from the sketch he supplied: two
// overlapping rings, and the wordmark "CivicLens" set solid — one word, two
// tints, no space.
//
// ONE COMPONENT FOR BOTH SIDES OF THE PRODUCT. The signed-in app (Primer
// React) and the marketing and auth pages (Primer react-brand) had separate
// marks that drifted; this replaces the lens mark that
// civic_lens/components/landing/LensMark.jsx used to draw, and that file now
// renders this.
//
// THE TWO TINTS. The deep tint is the brand blue where a brand token exists —
// react-brand's `--base-color-scale-blue-5` on the marketing pages — falling
// back to Primer's `--fgColor-accent` inside the app, where the brand scale is
// not loaded. The lighter tint is that same blue at reduced opacity rather
// than a second token: nothing in Primer guarantees a lighter blue that is
// legible in BOTH themes, and an opacity holds its relationship to whatever
// blue the theme is using. No hardcoded hex, in either place.
const DEEP = 'var(--base-color-scale-blue-5, var(--fgColor-accent))';

// THE LIGHTER TINT IS MIXED, NOT FADED (Cameron, 2026-09-19). It was the deep
// blue at 55% opacity, which works on a white page — fading towards white is
// what "lighter" means there — but on the dark page it fades towards the dark
// surface, so "Lens" and the second ring went DIMMER rather than lighter, and
// the lockup lost its second tone. Mixing with white instead lightens in both
// themes. `color-mix` is supported by every browser this app targets; where it
// is not, the declaration is dropped and both tones render deep, which is a
// duller lockup rather than a broken one.
const LIGHT = `color-mix(in srgb, ${DEEP} 55%, white)`;

// The marketing and auth pages load react-brand's face; the app does not. The
// wordmark asks for the brand heading face and falls back to whatever the
// surrounding UI is using — WITHOUT this, a plain span on the marketing pages
// fell back to the browser's serif, which is how the logged-out lockup came to
// be set in Times.
// Mona Sans first, then react-brand's own stack where it is defined, then the
// surrounding UI font. Named directly rather than through the brand token
// because the app does not define that token — only the marketing pages do —
// and the wordmark must be the same face in both.
// NOT `inherit` AS THE FALLBACK: `font-family: "Mona Sans", inherit` is an
// invalid declaration and the browser drops the WHOLE thing, so the wordmark
// silently fell back to the page's font with no Mona Sans at all. A real stack
// it is — react-brand's own, minus the tokens the app does not define.
const BRAND_FACE =
  '"Mona Sans", "MonaSansFallback", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

// `tone="onDark"` puts the mark on the dark hero block and the dark footer,
// where the brand blue goes muddy: the rings become the surface's own
// foreground, and the second ring keeps the same lighter tint of it.
const strokeFor = (tone) => (tone === 'onDark' ? 'var(--brand-color-text-default, var(--fgColor-onEmphasis))' : DEEP);

export function CivicLensMark({ size = 28, tone = 'default', title }) {
  const stroke = strokeFor(tone);
  return (
    <svg
      width={size}
      height={size * 0.68}
      viewBox="0 0 50 34"
      fill="none"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      style={{ flex: 'none', display: 'block', overflow: 'visible' }}
    >
      {/* Two rings of one size, overlapping by a third of their width — the
          proportion in the sketch. The stroke is heavy enough to read at 16px
          in the collapsed rail. */}
      <circle cx="17" cy="17" r="14" stroke={stroke} strokeWidth="5" />
      <circle
        cx="33"
        cy="17"
        r="14"
        stroke={tone === 'onDark' ? stroke : LIGHT}
        strokeWidth="5"
        opacity={tone === 'onDark' ? 0.55 : 1}
      />
    </svg>
  );
}

// "CivicLens" — one word. "Civic" in the deep tint, "Lens" in the lighter one,
// matching the two rings. Inherits the surrounding font so it reads as the
// product's own type rather than an imported face.
export function CivicLensWordmark({ fontSize = 20, tone = 'default' }) {
  const color = strokeFor(tone);
  return (
    <span
      style={{
        fontSize,
        fontWeight: 'var(--base-text-weight-semibold, 600)',
        letterSpacing: '-0.5px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        fontFamily: BRAND_FACE,
        color
      }}
    >
      Civic
      <span style={tone === 'onDark' ? { opacity: 0.55 } : { color: LIGHT }}>Lens</span>
    </span>
  );
}

// The mark and the wordmark together, which is what every surface but the
// collapsed rail uses. `as` lets it be a link (the marketing header) or a plain
// span (the app's own nav, where the brand is not a link).
export default function CivicLensBrand({
  as: Component = 'span',
  markSize = 28,
  fontSize = 20,
  tone = 'default',
  markOnly = false,
  gap = 8,
  ...props
}) {
  return (
    <Component
      {...props}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${gap}px`,
        textDecoration: 'none',
        minWidth: 0,
        ...(props.style || {})
      }}
    >
      <CivicLensMark size={markSize} tone={tone} />
      {!markOnly && <CivicLensWordmark fontSize={fontSize} tone={tone} />}
    </Component>
  );
}
